import React, { useMemo, useState } from 'react'
import {
  Button,
  Container,
  Divider,
  Grid,
  Header,
  Icon,
  Message,
  Progress,
  Transition,
} from 'semantic-ui-react'
import CopyToClipboard from 'react-copy-to-clipboard'
import {
  AbbreviatedProduct,
  AbbreviatedProductContainer,
  PRODUCT_SUBTYPE_OPTIONS,
  PRODUCT_TYPE_OPTIONS,
} from '../../consts'
import CurrentQueue from './CurrentQueue'
import OptionalInput from './OptionalInput'
import OptionalTextArea from './OptionalTextArea'
import OptionalImage from './OptionalImage'
import { useDispatch, useSelector } from 'react-redux'
import { postAProduct } from '../../redux/actions/products'
import { AppState } from '../../redux/store'
import { sleep } from '../../utils/sleep'
import LookupProduct from '../LookupProduct/LookupProduct'
import { toast } from 'react-toastify'
import OptionalSelect from './OptionalSelect'
import OptionalBrandSearch from './OptionalBrandSearch'
import { resetBulkEditResults } from '../../redux/actions/ui'
import { ProductType } from '../../types'
import OptionalLocalizedImage from './OptionalLocalizedImage'
import axios from 'axios'
import BulkEditResults from './BulkEditResults'

interface EditSettingObject {
  title: string
  isEditing: boolean
  value: string | null
  displayValue?: string
}

interface EditSettings {
  brand: EditSettingObject
  brandSubtype: EditSettingObject
  description: EditSettingObject
  image: EditSettingObject
  lineage: EditSettingObject
  localizedImage: EditSettingObject
  productSubtype: EditSettingObject
  productType: EditSettingObject
}
const initialEditSettings = {
  description: {
    title: 'Description',
    isEditing: false,
    value: null,
  },
  brand: {
    title: 'Brand',
    isEditing: false,
    value: null,
    displayValue: '',
  },
  brandSubtype: {
    title: 'Brand Subtype',
    isEditing: false,
    value: null,
  },
  productType: {
    title: 'Product Type',
    isEditing: false,
    value: null,
  },
  image: {
    title: 'Image',
    isEditing: false,
    value: null,
  },
  localizedImage: {
    title: 'State-Specific Image',
    isEditing: false,
    // value is stored as: "{COUNTRY}-{SUBDIVISION}|{IMAGE_URLS}"
    value: null,
  },
  lineage: {
    title: 'Lineage',
    isEditing: false,
    value: null,
  },
  productSubtype: {
    title: 'Product Subtype',
    isEditing: false,
    value: null,
  },
}

function getWarningMessage(queueSize: number, key: string, setting: EditSettingObject) {
  return (
    <Message
      key={key}
      color="orange"
      header={`You are editing the ${key} for ${queueSize} products`}
      content={`It will be set to "${setting.displayValue ?? setting.value}"`}
      hidden={!setting.isEditing || !setting.value}
    />
  )
}

const BulkEditor = () => {
  const dispatch = useDispatch()
  const [step, setStep] = useState<number>(0)
  const [retainOldImages, setRetainOldImages] = useState<boolean>(false)
  const [retainOldStateImages, setRetainOldStateImages] = useState<boolean>(false)

  const [editSettings, setEditSettings] = useState<EditSettings>(initialEditSettings)
  const [finalSubmitLoading, setFinalSubmitLoading] = useState<boolean>(false)
  const [copiedErrors, setCopiedErrors] = useState<boolean>(false)
  const [localeUpdateSuccesses, setLocaleUpdateSuccesses] = useState<any[]>([])
  const [localeUpdateErrors, setLocaleUpdateErrors] = useState<any[]>([])

  const { bulkEditSuccesses, bulkEditErrors } = useSelector((state: AppState) => state.ui) // as submissions for each product complete, bulkEditSuccesses and bulkEditErrors both are arrays of product IDs
  // so a product ID 123 in bulkEditSuccesses means that product 123 was successfully updated

  const canSubmit = useMemo(
    () => Object.values(editSettings).some((x) => x.value && x.isEditing),
    [editSettings]
  )

  const [productsQueue, setProductsQueue] = useState<AbbreviatedProductContainer>({})

  const addProductToProductsQueue = (abbrevProduct: AbbreviatedProduct | AbbreviatedProduct[]) => {
    const newQueue = { ...productsQueue }

    if (abbrevProduct instanceof Array) {
      abbrevProduct.forEach((ap) => {
        newQueue[ap.id] = ap
      })
      setProductsQueue(newQueue)
    } else {
      newQueue[abbrevProduct.id] = abbrevProduct
      setProductsQueue(newQueue)
    }
  }

  const removeProductFromProductsQueue = (id: number) => {
    const newQueue = { ...productsQueue }
    delete newQueue[id]
    setProductsQueue(newQueue)
  }

  const queueSize = useMemo(() => Object.keys(productsQueue).length, [productsQueue])

  const finalSubmitPercent = useMemo(
    () => Math.round(((bulkEditSuccesses.length + bulkEditErrors.length) / queueSize) * 100),
    [bulkEditErrors.length, bulkEditSuccesses.length, queueSize]
  )

  const stateSpecificSubmitPercent = useMemo(
    () =>
      Math.round(((localeUpdateErrors.length + localeUpdateSuccesses.length) / queueSize) * 100),
    [localeUpdateErrors.length, localeUpdateSuccesses.length, queueSize]
  )

  const nextStepBtn = () =>
    queueSize && !step ? (
      <Button primary onClick={() => setStep(1)}>
        Done Building Queue
      </Button>
    ) : null

  const clearCartBtn = () =>
    queueSize && !step ? (
      <Button color="yellow" onClick={() => setProductsQueue({})}>
        Clear Queue
      </Button>
    ) : null

  function onClickEditOption(key: keyof EditSettings) {
    setEditSettings({
      ...editSettings,
      [key]: {
        ...editSettings[key],
        isEditing: !editSettings[key].isEditing,
      },
    })
  }

  function onChangeEditOption(key: keyof EditSettings, value: string, displayValue?: string) {
    const newEditSetting = { ...editSettings }[key]
    newEditSetting.value = value
    if (displayValue !== undefined) {
      newEditSetting.displayValue = displayValue
    }
    setEditSettings({
      ...editSettings,
      [key]: newEditSetting,
    })
  }

  function formDataBuilder(product: AbbreviatedProduct) {
    const formData = new FormData()

    formData.append('id', product.id.toString())

    if (editSettings.description.value && editSettings.description.isEditing) {
      formData.append('description', editSettings.description.value.trim())
    }

    if (editSettings.brand.value && editSettings.brand.isEditing) {
      formData.append('product_brand_id', editSettings.brand.value)
    }

    if (editSettings.brandSubtype.value && editSettings.brandSubtype.isEditing) {
      formData.append('kind_subtype', editSettings.brandSubtype.value.trim())
    } else {
      // we have to set a kind_subtype (aka category_label), otherwise it will be overwritten by the monolith
      formData.append('kind_subtype', product.brand_subtype || '')
    }

    if (editSettings.lineage.value && editSettings.lineage.isEditing) {
      formData.append('category', editSettings.lineage.value)
    } else {
      // we have to set a category (aka lineage), otherwise it will be overwritten by the monolith
      formData.append('category', product.lineage || '')
    }

    if (editSettings.image.value && editSettings.image.isEditing) {
      if (retainOldImages) {
        product.image_urls?.forEach((url: string) => {
          formData.append('image_urls[]', url)
        })
      }

      JSON.parse(editSettings.image.value ?? '[]').forEach((url: string) => {
        formData.append('image_urls[]', url)
      })
    }

    if (editSettings.productType.value && editSettings.productType.isEditing) {
      formData.append('kind', editSettings.productType.value)
    }

    if (editSettings.productSubtype.value && editSettings.productSubtype.isEditing) {
      formData.append('root_subtype', editSettings.productSubtype.value)
    }

    return formData
  }

  async function onFinalSubmission() {
    setFinalSubmitLoading(true) // finalSubmitLoading stays true upon submission
    let count = 0
    for (const product of Object.values(productsQueue)) {
      const { id } = product
      count += 1
      if (count % 75 === 0) {
        await sleep(10000)
      }
      if (editingNonLocale) {
        const formData = formDataBuilder(product)

        dispatch(
          postAProduct({
            formData,
            mode: 'edit',
            isBatchLookup: false,
            isBulkEdit: true,
          })
        )
      }
      if (editSettings.localizedImage.isEditing && editSettings.localizedImage.value) {
        // value is stored as: "{COUNTRY}-{SUBDIVISION}|{IMAGE_URLS}"
        const [country, subdivision] = editSettings.localizedImage.value?.split('|')[0].split('-')
        const images = editSettings.localizedImage.value?.split('|')[1]
        const data: any = {
          product_id: id,
          retain: retainOldStateImages,
          country,
          subdivision,
          images,
        }
        try {
          const response = await axios.put('/api/catalog_product_localizations', data)
          if (response.data?.success !== false) {
            setLocaleUpdateSuccesses((oldValue) => [...oldValue, id])
          } else {
            setLocaleUpdateErrors((oldValue) => [...oldValue, id])
          }
        } catch (e) {
          setLocaleUpdateErrors((oldValue) => [...oldValue, id])
          console.error(e)
        }
      }
    }
  }

  const refresh = (args?: { keepQueue?: boolean }) => {
    setStep(0)
    if (!args?.keepQueue) {
      setProductsQueue({})
    }
    setEditSettings(initialEditSettings)
    dispatch(resetBulkEditResults())
    setFinalSubmitLoading(false)
    setLocaleUpdateErrors([])
    setLocaleUpdateSuccesses([])
    setRetainOldImages(false)
    setRetainOldStateImages(false)
  }

  const gridWithCart = (child: any) => (
    <Grid columns={2} divided>
      <Grid.Column width={12}>{child}</Grid.Column>
      <Grid.Column width={4}>
        <Header as="h2">
          Current Queue
          <Header.Subheader className="bulk-editor-queue-count">
            {`${queueSize} Products In Queue`}
          </Header.Subheader>
        </Header>
        <Grid stackable columns="equal">
          <Grid.Column>{nextStepBtn()}</Grid.Column>
          <Grid.Column textAlign="right">{clearCartBtn()}</Grid.Column>
        </Grid>
        <CurrentQueue
          cart={productsQueue}
          removeFromCart={(id: number) => removeProductFromProductsQueue(id)}
          isLoading={finalSubmitLoading}
        />
      </Grid.Column>
    </Grid>
  )
  function getStepOneBuildQueue() {
    return gridWithCart(
      <LookupProduct
        addProductToBulkEditProductsQueue={(
          abbrevProduct: AbbreviatedProduct | AbbreviatedProduct[]
        ) => addProductToProductsQueue(abbrevProduct)}
        bulkEditProductsQueue={productsQueue}
        mode="bulkEdit"
        removeProductFromBulkEditProductsQueue={(id: number) => removeProductFromProductsQueue(id)}
        onModalActiveChange={() => null}
      />
    )
  }

  const editingNonLocale = useMemo(
    () =>
      Object.keys(editSettings).some(
        (eachKey) =>
          eachKey !== 'localizedImage' && editSettings[eachKey as keyof EditSettings].isEditing
      ),
    [editSettings]
  )

  function getStepTwoChooseEdits() {
    return (
      <Container>
        <OptionalTextArea
          inputName="Description"
          isEditing={editSettings.description.isEditing}
          onClick={() => onClickEditOption('description')}
          onChange={(value: string) => onChangeEditOption('description', value)}
          value={editSettings.description.value || ''}
        />

        <OptionalBrandSearch
          inputName="Brand"
          isEditing={editSettings.brand.isEditing}
          onClick={() => onClickEditOption('brand')}
          onChange={(value: string, displayValue: string) =>
            onChangeEditOption('brand', value, displayValue)
          }
          value={editSettings.brand.value || ''}
        />

        <OptionalInput
          inputName="Brand Subtype"
          isEditing={editSettings.brandSubtype.isEditing}
          onClick={() => onClickEditOption('brandSubtype')}
          onChange={(value: string) => onChangeEditOption('brandSubtype', value)}
          value={editSettings.brandSubtype.value || ''}
        />

        <div style={{ marginTop: '15px' }}>
          <OptionalSelect
            isEditing={editSettings.lineage.isEditing}
            onClick={() => onClickEditOption('lineage')}
            onChange={(value: string) => onChangeEditOption('lineage', value)}
            options={['indica', 'cbd', 'hybrid', 'sativa'].map((each) => ({
              key: each,
              value: each,
              text: each,
            }))}
            placeholder="Select lineage"
            inputName="lineage"
            value={editSettings.lineage.value || ''}
          />
        </div>

        <div style={{ marginTop: '15px' }}>
          <OptionalSelect
            isEditing={editSettings.productType.isEditing}
            onClick={() => onClickEditOption('productType')}
            onChange={(value: string) => onChangeEditOption('productType', value)}
            options={PRODUCT_TYPE_OPTIONS.filter((option) => option.key).map((option) => ({
              key: option.key,
              text: option.text,
              value: option.value,
            }))}
            placeholder="Select product type"
            inputName="product type"
            value={editSettings.productType.value || ''}
          />
        </div>

        <div
          style={{
            marginTop: '15px',
            display: editSettings.productType.isEditing ? 'block' : 'none',
          }}
        >
          <OptionalSelect
            isEditing={editSettings.productSubtype.isEditing}
            onClick={() => onClickEditOption('productSubtype')}
            onChange={(value: string) => onChangeEditOption('productSubtype', value)}
            options={
              editSettings.productType.value
                ? PRODUCT_SUBTYPE_OPTIONS[editSettings.productType.value as ProductType]
                : []
            }
            placeholder="Select product subtype"
            inputName="product subtype"
            value={editSettings.productSubtype.value || ''}
          />
        </div>

        <div>
          <OptionalImage
            isEditing={editSettings.image.isEditing}
            onClick={() => onClickEditOption('image')}
            onChange={(value: string) => onChangeEditOption('image', value)}
            inputName={editSettings.image.title}
            value={editSettings.image.value}
          />
          <div hidden={!editSettings.image.isEditing}>
            {
              <label htmlFor="retainImages">
                Retain Original Images for Each Product
                <input
                  type="checkbox"
                  id="retainImages"
                  checked={retainOldImages}
                  onClick={() => setRetainOldImages(!retainOldImages)}
                />
              </label>
            }
          </div>
        </div>

        <div>
          <OptionalLocalizedImage
            isEditing={editSettings.localizedImage.isEditing}
            onClick={() => onClickEditOption('localizedImage')}
            onChange={(value: string) => onChangeEditOption('localizedImage', value)}
            inputName={editSettings.localizedImage.title}
            value={editSettings.localizedImage.value}
          />
          <div hidden={!editSettings.localizedImage.isEditing}>
            {
              <label htmlFor="retainImages">
                Retain Original State-Specific Images for Each Product
                <input
                  type="checkbox"
                  id="retainStateImages"
                  checked={retainOldStateImages}
                  onClick={() => setRetainOldStateImages(!retainOldStateImages)}
                />
              </label>
            }
          </div>
        </div>

        <Button
          fluid
          onClick={() => setStep(0)}
          secondary
          style={{ margin: '50px auto 10px auto' }}
        >
          Go back
        </Button>

        <Button
          fluid
          primary
          disabled={!canSubmit}
          onClick={() => setStep(2)}
          style={{ margin: '10px auto' }}
        >
          Continue with edits
        </Button>
      </Container>
    )
  }

  function getStepThreeSubmitEdits() {
    return gridWithCart(
      <div>
        <h2>Submit Changes</h2>
        <Message
          warning
          header={`You are about to permanently change ${queueSize} products.`}
          content="There's no turning back!"
        />
        <div>
          {/*Warning messages...*/}
          {Object.keys(editSettings)
            .filter((key) => key !== 'image' && key !== 'localizedImage')
            .map((key) =>
              getWarningMessage(
                queueSize,
                editSettings[key as keyof EditSettings].title.toUpperCase(),
                editSettings[key as keyof EditSettings]
              )
            )}

          <Message
            color="orange"
            header={`You are editing the images for ${queueSize} products`}
            content={
              <>
                <span>New images: </span>
                {JSON.parse(editSettings.image.value ?? '[]').map((url: string, key: number) => (
                  <img key={`${url}/${key}`} src={url} width={200} alt="Your upload" />
                ))}
              </>
            }
            hidden={!editSettings.image.isEditing || !editSettings.image.value}
          />

          <Message
            color="orange"
            header={`You are editing the STATE-SPECIFIC images for ${queueSize} products in locale ${
              editSettings.localizedImage.value?.split('|')[0]
            }`}
            content={
              <>
                <span>New images: </span>
                {JSON.parse(editSettings.localizedImage.value?.split('|')[1] ?? '[]').map(
                  (url: string, key: number) => (
                    <img key={`${url}/${key}`} src={url} width={200} alt="Your upload" />
                  )
                )}
              </>
            }
            hidden={!editSettings.localizedImage.isEditing || !editSettings.localizedImage.value}
          />
        </div>
        {(finalSubmitLoading || finalSubmitPercent === 100) && editingNonLocale && (
          <>
            <Progress
              label={'Normal Content'}
              indicating={finalSubmitPercent < 100}
              progress={finalSubmitPercent < 100}
              percent={finalSubmitPercent}
              color="green"
            />
          </>
        )}

        {editSettings.localizedImage.isEditing &&
          editSettings.localizedImage.value?.split('|')[1].length &&
          (finalSubmitLoading || finalSubmitPercent === 100) && (
            <>
              <Progress
                label={'State-Specific Content'}
                indicating={finalSubmitPercent < 100}
                progress={finalSubmitPercent < 100}
                percent={stateSpecificSubmitPercent}
                color="green"
              />

              <div>
                <i>There is a 10s sleep every 75 products.</i>
              </div>
            </>
          )}

        <Transition animation="tada" duration={2000} visible={finalSubmitPercent === 100}>
          <Message positive>Complete!</Message>
        </Transition>

        <div style={{ marginTop: '15px' }}>
          <Button
            disabled={finalSubmitLoading}
            primary
            onClick={() => {
              onFinalSubmission()
            }}
          >
            Submit changes for {queueSize} products
          </Button>
        </div>

        <div style={{ marginTop: '15px' }}>
          <Button secondary disabled={finalSubmitLoading} onClick={() => setStep(1)}>
            Go back
          </Button>
        </div>
        <div style={{ marginTop: '15px' }}>
          <Button color="orange" onClick={() => refresh()}>
            Reset Bulk Editor
          </Button>
          <Button color="yellow" onClick={() => refresh({ keepQueue: true })}>
            Reset Bulk Editor &amp; Keep Queue
          </Button>
        </div>

        {localeUpdateErrors.length ? (
          <Message error>
            State-specific content updates failed for the following products:
            <br />
            {localeUpdateErrors.join(', ')}
          </Message>
        ) : null}

        {editingNonLocale && (
          <>
            <div
              style={{
                marginTop: '15px',
                display: `${bulkEditErrors.length && !copiedErrors ? '' : 'none'}`,
              }}
            >
              <CopyToClipboard
                text={
                  'Id,Name,Brand,ErrorMsg\n' +
                  bulkEditErrors
                    .map(
                      ({ id, message }) =>
                        `${productsQueue[id].id},${productsQueue[id].name},${productsQueue[id].brand},${message}`
                    )
                    .join('\n')
                }
                onCopy={() => {
                  setCopiedErrors(true)
                  toast(`Copied errors to clipboard`, {
                    containerId: 'default',
                    toastId: 'copied-bulk-editor-errors',
                  })
                  setTimeout(() => setCopiedErrors(false), 2000)
                }}
              >
                <Button color="teal">Copy Error Rows as CSV</Button>
              </CopyToClipboard>
            </div>

            <div style={{ marginTop: '25px' }}>
              <BulkEditResults productsQueue={productsQueue} />
            </div>
          </>
        )}
      </div>
    )
  }

  const getBody = () => {
    switch (step) {
      case 0:
        return getStepOneBuildQueue()
      case 1:
        return getStepTwoChooseEdits()
      case 2:
        return getStepThreeSubmitEdits()
    }
  }

  return (
    <div style={{ padding: 20 }}>
      <Container textAlign="center">
        <Header id="bulk-editor-header" as="h1" textAlign="center">
          <Icon name="pencil square" circular style={{ display: 'inline-block' }} />
          <Header.Content style={{ display: 'inline-block' }}>Bulk Editor</Header.Content>
        </Header>
      </Container>

      <Divider />

      {getBody()}
    </div>
  )
}

export default BulkEditor
